import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { LinearGradient } from 'expo-linear-gradient';
import { useRef, useState, type ReactNode } from 'react';
import type {
  DimensionValue,
  LayoutChangeEvent,
  NativeScrollEvent,
  NativeSyntheticEvent,
} from 'react-native';

import { ScrollView } from '@/components/ui/scroll-view';
import { View } from '@/components/ui/view';
import { useColors } from '@/hooks/useColors';

const EDGE_FADE_WIDTH = 28;

const EDGE_TOLERANCE = 1;

/**
 * Horizontal scroller that fades each edge that has more content beyond it, so a strip that
 * overflows reads as scrollable. On web it also shows a thin themed scrollbar, since a mouse
 * has no horizontal wheel to find the overflow by.
 */
export function FadedHorizontalScroll({
  minContentWidth,
  children,
}: {
  minContentWidth: DimensionValue;
  children: ReactNode;
}) {
  const { colors } = useColors();

  const metrics = useRef({ offset: 0, viewport: 0, content: 0 });

  const [hasMoreBefore, setHasMoreBefore] = useState(false);

  const [hasMoreAfter, setHasMoreAfter] = useState(false);

  const fadeColor = colors.background[50];

  const updateFades = (patch: Partial<typeof metrics.current>) => {
    metrics.current = { ...metrics.current, ...patch };

    const { offset, viewport, content } = metrics.current;

    setHasMoreBefore(offset > EDGE_TOLERANCE);

    setHasMoreAfter(offset + viewport < content - EDGE_TOLERANCE);
  };

  const handleScroll = (event: NativeSyntheticEvent<NativeScrollEvent>) =>
    updateFades({ offset: event.nativeEvent.contentOffset.x });

  const handleLayout = (event: LayoutChangeEvent) =>
    updateFades({ viewport: event.nativeEvent.layout.width });

  const webScrollbarStyle = isWeb
    ? ({ scrollbarWidth: 'thin', scrollbarColor: `${colors.primary[400]} transparent` } as object)
    : undefined;

  const fadeStyle = { position: 'absolute', top: 0, bottom: 0, width: EDGE_FADE_WIDTH } as const;

  return (
    <View>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={isWeb}
        scrollEventThrottle={16}
        style={webScrollbarStyle}
        onScroll={handleScroll}
        onLayout={handleLayout}
        onContentSizeChange={(content) => updateFades({ content })}
        contentContainerStyle={{ minWidth: minContentWidth }}>
        {children}
      </ScrollView>
      {hasMoreBefore ? (
        <LinearGradient
          pointerEvents="none"
          colors={[fadeColor, `${fadeColor}00`]}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 0 }}
          style={{ ...fadeStyle, left: 0 }}
        />
      ) : null}
      {hasMoreAfter ? (
        <LinearGradient
          pointerEvents="none"
          colors={[`${fadeColor}00`, fadeColor]}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 0 }}
          style={{ ...fadeStyle, right: 0 }}
        />
      ) : null}
    </View>
  );
}
